<template>
  <NuxtLoadingIndicator color="linear-gradient(90deg,#ff582b,#ff2642)" />
  <NuxtLayout>
    <div
      v-show="useRouterLoading().value || useRequestLoading().value || eventPreorderLoading"
      class="router-jump-animation"
    >
      <div :class="useIsMobile().value ? 'loading-box loading-box-mobile' : 'loading-box'">
        <img :src="$cvWebpFn(`https://img.kilimall.com/c/h5/loading_default.png`, { w: 30 })" >
        <div class="process-box"></div>
      </div>
    </div>

    <NuxtPage></NuxtPage>
    <GoogleLogin />
    <ClientOnly>
      <LazyPWAPromptPopup v-if="useIsMobile().value" />
      <PCLiveChat v-if="!useIsMobile().value" />
    </ClientOnly>
  </NuxtLayout>
</template>
<script setup lang="ts">
import md5 from 'js-md5'
import { getShipingCartTotal, getUserProfile } from '@/api/models/userInfo'
import { getGlobalCatch, cartList } from '@/api/models/system'
import { better8DigitId } from './plugins/utils'
import { getDefaultSiteTitle } from './utils/site-title'
import { ssrNow, ssrTimingLog } from './utils/ssr-timing'
import { ensureWapListingDetailVersion } from './utils/wap-listing-detail-version'
const PCLiveChat = defineAsyncComponent(() => import('./components/PC/LiveChat/index.vue'))

const nuxtApp = useNuxtApp()
const route = useRoute()
const eventPreorderLoading = useState<boolean>('event-preorder-pc-loading', () => false)

const { $formatShoppingCartList, $initOpenByFlutter, $asyncLoadJs } = useNuxtApp()

if(process.client) {
  useRouterLoading().value = false
}

if(!useCookie('kilimall_cache_key').value) {
  useCookie('kilimall_cache_key').value = better8DigitId()
  if(route.query?.development === '1') {
    console.log('-- 调试模式日志输出 --')
    $asyncLoadJs('/js/vconsole.min.js').then(() => {
      var vConsole = new window.VConsole(); 
    }).catch((error: any) => {
      console.log('调试模式 -- vConsole -- error: ', error)
    })
  }
}

watch(
  () => route.fullPath,
  () => {
    useRouterLoading().value = false
  }
)

// /* $initializeFreshChat */
const initOpenByAndroid = () => {
  const ui = window.androidWebObject?.getAuthInfo()
  useCookie('UserInfo').value = ui

  const rid = window.androidWebObject?.getRegionId()
  useCookie('regionId').value = rid

  try {
    useUserInfo().value = JSON.parse(ui)
  } catch(e: any) {
    console.error(e)
  }
}

/** 无购物车展示的页面，跳过 cart/items-count、v3/cart 等请求 */
const CART_API_SKIP_PATHS = [
  /^\/event(?:\/|$)/,
  /^\/login(?:\/|$)/,
  /^\/checkCode(?:\/|$)/,
  /^\/login-error(?:\/|$)/,
  /^\/quick-reg-binding(?:\/|$)/,
  /^\/set-pwd(?:\/|$)/,
  /^\/set-invitation(?:\/|$)/,
  /^\/oauth2-authorize(?:\/|$)/
]

const shouldSkipCartApi = (path: string) =>
  CART_API_SKIP_PATHS.some((pattern) => pattern.test(path))

const initShoppingCart = async() => {
  if (shouldSkipCartApi(route.path)) {
    return
  }
  // 如果是购物车列表，则不需要初始化，否则会重复调用
  if(!/\/shopping-cart/.test(route.path) && !useIsMobile().value) {
    const r = await cartList(false, process.server);

    $formatShoppingCartList(r.stores || [])
  }
}

if(process.client) {
  if(window.androidWebObject) {
    // 是否为 Android app 打开(要区分 卖家 和 买家 APP)

    window.androidWebObject.isSeller || !window.androidWebObject?.getAuthInfo
      ? useIsSellerAndroid().value = true
      : useIsAndroid().value = true 
  } else {
    if (window.flutter_inappwebview) {
      useIsFlutterApp().value = true

      await $initOpenByFlutter()
    } else {
      // 如果未准备好，监听事件
      window.addEventListener("flutterInAppWebViewPlatformReady", async() => {
        useIsFlutterApp().value = true
        await initOpenByFlutter()
      })
    }
  }
  useIsAndroid().value && initOpenByAndroid()

  setTimeout(() => {
    const initTasks: Promise<unknown>[] = [initShoppingCart()]

    if (!shouldSkipCartApi(route.path)) {
      initTasks.push(
        getShipingCartTotal().then((r: any) => {
          useShopingCartTotal().value = r?.count || 0
        })
      )
    }

    Promise.all(initTasks)

    nuxtApp.$msgSocket.createSocket(nuxtApp)
  }, 50)
}

// 服务端初始化全局缓存数据
// 说明：app.vue 不能 import server/*（Nuxt 限制）；进程内直调在 /api/global-cache → getHomeJSONData 完成
let seoHeadApplied = false
try {
  const globalStart = ssrNow()
  const globalRes = await getGlobalCatch()
  ssrTimingLog('app.getGlobalCatch', {
    ms: ssrNow() - globalStart,
    path: route.path,
    server: process.server
  })

  const { obsJsonData, regions }  = globalRes
  useCountrys().value = regions || []
  useGlobalJsonData().value = obsJsonData

  if(obsJsonData.seo?.globalConfig) {
    const { title: seoTitle, metaDescription, metaTitle, metaKeywords } = obsJsonData.seo.globalConfig

    if(seoTitle) {
      seoHeadApplied = true
      useHead({
        title: seoTitle,
        meta: [
          { name: 'title', content: metaTitle },
          { name: 'description', content: metaDescription },
          { name: 'keywords', content: metaKeywords }
        ]
      })
    }
  }
} catch(e) {
  console.error('wap--init region: ', e)
}

if(process.server) {
  // 根据 host 取国家
  const { host } = useRequestHeaders()

  const currentCountry = useCountrys().value.find((c: region.IRegionItem) => {
    return c.domain === `https://${host}` || c.domain === `http://${host}`
  })

  if(currentCountry) {
    const { id: rid, code: countryCode } = currentCountry

    useCookie('regionId').value = String(rid)
    useCookie('regionCode').value = countryCode
    useCurrentCountry().value = rid
    useCurrentCountryCode().value = countryCode
  }
}

if(!seoHeadApplied) {
  const siteTitle = { title: getDefaultSiteTitle() }
  if(import.meta.server) {
    useServerHead(siteTitle)
  } else {
    useHead(siteTitle)
  }
}

const anId = useCookie('anonymous-id').value
if(!anId) {
  useCookie('anonymous-id').value = md5(new Date().getTime() + '_anonymous-id')
}
watch(
  () => useUserInfo().value,
  async(val: IUserInfo | null, oldVal: IUserInfo | null) => {
    if(/\/pvHome/.test(route.path)) {
      return
    }

    if(val?.accessToken) {
      nuxtApp.$msgSocket.clearSocket()
      nuxtApp.$msgSocket.createSocket(nuxtApp)
      
      nuxtApp.$setProfile && nuxtApp.$setProfile(nuxtApp.$sensors)
      
      initShoppingCart()
    } else if (oldVal?.accessToken && process.client) {
      nuxtApp.$setProfile && nuxtApp.$setProfile(nuxtApp.$sensors)
      if (!shouldSkipCartApi(route.path)) {
        initShoppingCart()
      }
    }

    if(process.client) {
      // Token有变化,需要清除掉 缓存的老的 K系统 Token
      localStorage.removeItem('cache_old_token')

      initSSQ().catch(() => console.warn('Unable to initialize customer service'))
    }
  }
)

const initTikTok = () => {
  const userInfo = useUserInfo().value
  const userId = userInfo?.userId?.trim() || ''
  const phone = userInfo?.phone || ''
  const profileEmail = userInfo?.profile?.email
  const email = typeof profileEmail === 'string' ? profileEmail : ''

  if (!userId) { return }

  // TikTok：传原始值时由 SDK 侧散列；phone_number 须为 E.164（+{国码}{本地号，去掉前导0}）
  const regionId = useCurrentCountry().value
  const currentCountry = useCountrys().value.find((c: region.IRegionItem) => c.id === Number(regionId))
  const callingCode = currentCountry?.callingCode || ''
  const phoneDigits = String(phone).replace(/\D/g, '').replace(/^0+/, '')
  const phoneNumber = callingCode && phoneDigits ? `+${callingCode}${phoneDigits}` : ''

  const ttqParams = {
    external_id: userId,
    ...(email ? { email } : {}),
    ...(phoneNumber ? { phone_number: phoneNumber } : {})
  }
  window.ttq.identify(ttqParams)
  console.log('ttqParams', ttqParams)
  
}

const initSSQ = async() => {
  // The startup plugin previously completed before app setup. Keep that SDK
  // preparation point while allowing the page itself to hydrate independently.
  await ensureWapListingDetailVersion()
  // https://app.salesmartly.com/chat 客户插件
  let chatUrl = 'https://assets.salesmartly.com/js/project_147115_152567_1728893627.js'
  if (useCurrentCountryCode().value === 'UG') {
    chatUrl = 'https://plugin-code.salesmartly.com/js/project_147115_526359_1761621653.js'
  }
  await $asyncLoadJs(chatUrl, { ready: () => !!(window.ssq && window.__ssc) })
  if(typeof window === undefined || !window.ssq) {
    return
  }
  window.__ssc.setting = { hideIcon: true };

  useSSQ().value = window.ssq
  const { phone='', userId='', email='', nick='' } = useUserInfo().value || {}

  useSSQ().value.push('setLoginInfo', {
    user_id: userId, // 加密后的用户id, 必填！
    user_name: nick, // 对应用户名
    phone: phone, // 对应用户手机号
    email: email, // 对应用户邮箱
    language: 'en-US',
    description: '',
    label_names: []
  })

  useSSQ().value.push('onUnRead', (obj: any) => {
    useSSQunRead().value = obj.num
  })
}

if(process.client) {
  initSSQ().catch(() => console.warn('Unable to initialize customer service'))
  ensureWapListingDetailVersion().then(initTikTok).catch(() => console.warn('Unable to prepare analytics identity'))
}
</script>

<style lang="scss">
#_app {
  background: #F5F8FB;
}

.router-jump-animation{
  position: fixed;
  z-index: 100001;
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;
  background-color: rgba($color: #000000, $alpha: 0.05);
  @include center();
  .loading-box-mobile{
    transform: scale(0.7);
  }
  .loading-box{
    width: 120px;
    height: 120px;
    background-color: #fff;
    margin-top: -200px;
    border-radius: 50%;
    box-shadow: 0 0 12px #999;
    @include center();
    position: relative;
    img{ width: 50%; }
    .process-box{
      position: absolute;
      top: -2px;
      left: -2px;
      height: 124px;
      width: 124px;
      background-image: url('https://img.kilimall.com/c/h5/loading_process_round.png?x-image-process=image/format,webp/resize,w_60');
      background-size: 100%;
      animation: rotate 0.8s linear infinite;
      @keyframes rotate { 100% { transform: rotate(360deg); } }
    }
  }
}
</style>
